import axios from 'axios';

const USE_DEV2 = import.meta.env.PUBLIC_USE_DEV2 === 'true';

const apiClient = axios.create({
  baseURL: import.meta.env.PUBLIC_API_URL,
  headers: {
    'Content-Type': 'application/json',
    dev2: USE_DEV2 ? 'true' : 'false',
  },
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
  return config;
});

apiClient.interceptors.request.use((config) => {
  if (USE_DEV2) {
    config.headers.dev2 = `true`;
    config.headers['dev2'] = `true`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = {
      requestMethod: error.config?.method?.toUpperCase() ?? null,
      requestUrl: error.config?.url ?? null,
      requestQueryParams: error.config?.params ?? null,
      requestHeaders: error.config?.headers ?? null,
      requestResponseType: error.config?.responseType ?? null,
      errorCode: error.code ?? null,
      errorMessage:
        error?.response?.data?.message ??
        error.message ??
        'An error occurred while processing the request.',
      responseStatusCode: error.response?.status ?? error.status ?? null,
      responseBody: error.response?.data ?? null,
    };
    console.error('API Client Error:', errorMessage);
    return Promise.reject(error);
  },
);

// Dev-only API call tracker to detect duplicate calls during page refresh
if (process.env.NODE_ENV === 'development') {
  const apiCallCounts = new Map();
  window.__apiCallTracker = {
    incr: (key) => apiCallCounts.set(key, (apiCallCounts.get(key) || 0) + 1),
    getAll: () => Array.from(apiCallCounts.entries()),
    dump: () => {
      const results = Array.from(apiCallCounts.entries())
        .map(([k, v]) => ({ call: k, count: v }))
        .sort((a, b) => b.count - a.count);
      // show only duplicates
      const duplicates = results.filter((r) => r.count > 1);
      console.table(duplicates.length ? duplicates : results.slice(0, 50));
      return duplicates.length ? duplicates : results.slice(0, 50);
    },
    reset: () => apiCallCounts.clear(),
  };

  apiClient.interceptors.request.use((config) => {
    try {
      const method = (config.method || 'GET').toUpperCase();
      const params = config.params ? JSON.stringify(config.params) : '';
      const key = `${method} ${config.url} ${params}`;
      window.__apiCallTracker.incr(key);
    } catch (err) {
      // ignore tracker errors
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const method = (error.config?.method || 'GET').toUpperCase();
        const params = error.config?.params ? JSON.stringify(error.config.params) : '';
        const key = `${method} ${error.config?.url} ${params}`;
        window.__apiCallTracker.incr(`${key} (ERROR)`);
      } catch (err) {}
      return Promise.reject(error);
    },
  );
}

// Re-export helpers for convenience/compatibility
export { clearApiCallRecords, logApiCallTotals } from './util/apiThrottle';

export default apiClient;
